import { useNavigate } from 'react-router';
import { toggleCreateTemplateDialog } from 'rtk/layout/LayoutControlSlice';
import { useCreateTemplateMutation } from 'rtk/query/apis/templates';
import { useAppDispatch } from 'rtk/query/hooks';
import { toast, type ExternalToast } from 'sonner';
import { z } from 'zod';
import { zCreateTemplate, type TCreateTemplateForm, type TCreateTemplatePayload } from '../types';
import { getCopyAudienceProblems } from '../parseCopyRejection';
import type { TTemplateAudienceProblem } from 'types/template/templateAudience.types';

type Params = {
  newTemplate: TCreateTemplateForm;
  isHubertAdmin: boolean;
};

export type CreateTemplateResponse = {
  templateId: string | null;
  isError?: boolean;
  message?: string;
  warnings?: string[];
};

export type TCreateTemplateSubmitResult = {
  isSuccess: boolean;
  templateId: string | null;
  message: string;
  warnings: string[];
  errors: string[];
  baseTemplateId?: string;
  name?: string;
  retryPayload?: TCreateTemplatePayload;
  audienceProblems?: TTemplateAudienceProblem[];
};

const getRejectedMessage = (rejected: unknown) => {
  if (
    rejected &&
    typeof rejected === 'object' &&
    'message' in rejected &&
    typeof (rejected as { message: unknown }).message === 'string'
  ) {
    return (rejected as { message: string }).message;
  }
  return 'Something went wrong while creating the template.';
};

export const useCreateTemplateSubmit = ({ newTemplate, isHubertAdmin }: Params) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createTemplateRequest, { isLoading }] = useCreateTemplateMutation();

  const onCreateTemplate = async (): Promise<TCreateTemplateSubmitResult> => {
    const { visibility } = newTemplate;

    if (visibility === 'local' && !newTemplate.accessGroupIds?.length) {
      const message = 'Access group is required!';
      toast.error(message, {
        position: 'top-center',
      });
      return {
        isSuccess: false,
        templateId: null,
        message,
        warnings: [],
        errors: [message],
      };
    }

    try {
      zCreateTemplate.parse({
        mode: newTemplate.mode,
        name: newTemplate.name.trim(),
        type: newTemplate.type,
        client_id: newTemplate.mode === 'create' ? newTemplate.sourceClientId : undefined, //only can be set for 'create' mode
        targetClientId: newTemplate.mode === 'copy' ? newTemplate.destinationClientId : undefined, // only can be set for 'copy' mode
        baseTemplateId: newTemplate.baseTemplateId,
        accessGroupIds: newTemplate.accessGroupIds,
        sourceScope: newTemplate.sourceScope,

        languageList:
          newTemplate.mode === 'copy' && newTemplate?.supportedLanguages ? newTemplate.supportedLanguages : undefined, // only can be set for 'copy' mode
        allowedLanguages:
          newTemplate.mode === 'copy' && newTemplate?.allowedLanguages ? newTemplate.allowedLanguages : undefined, // only can be set for 'copy' mode
        companyName: newTemplate.mode === 'copy' && newTemplate?.companyName ? newTemplate.companyName : undefined, // only can be set for 'copy' mode
        roleName: newTemplate.mode === 'copy' && newTemplate?.roleName ? newTemplate.roleName : undefined, // only can be set for 'copy' mode
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors = error.issues.map((issue) => `Data error: ${issue.message}`);
        errors.forEach((issue) => {
          toast.error(issue);
        });
        return {
          isSuccess: false,
          templateId: null,
          message: 'Validation failed.',
          warnings: [],
          errors,
        };
      }
      const message = 'Template validation failed.';
      toast.error(message);
      return {
        isSuccess: false,
        templateId: null,
        message,
        warnings: [],
        errors: [message],
      };
    }

    const payload: TCreateTemplatePayload = {
      name: newTemplate.name.trim(),
      type: newTemplate.type,
      client_id: isHubertAdmin ? newTemplate.sourceClientId : newTemplate.destinationClientId,
      targetClientId: newTemplate.destinationClientId,
      baseTemplateId: newTemplate?.baseTemplateId,
      accessGroupIds: newTemplate?.accessGroupIds,
      languageList: newTemplate?.supportedLanguages,
      allowedLanguages: newTemplate?.allowedLanguages,
      roleName: newTemplate?.roleName,
      companyName: newTemplate?.companyName,
    };

    try {
      const result = (await createTemplateRequest(payload).unwrap()) as CreateTemplateResponse;
      const templateId = result.templateId;
      const message = result.message ?? '';
      const warnings = result.warnings ?? [];
      const isSuccess = !result.isError && Boolean(templateId && templateId.length);

      if (!isSuccess) {
        const errorMessage = message || 'Template creation failed.';
        toast.error(errorMessage);
        return {
          isSuccess: false,
          templateId: null,
          message: errorMessage,
          warnings,
          errors: [errorMessage],
          retryPayload: payload,
        };
      }

      const toastOptions: ExternalToast = {
        duration: Infinity,
      };

      dispatch(toggleCreateTemplateDialog({ show: false, data: {} }));

      if (warnings.length) {
        warnings.forEach((warning) => {
          toast.warning(`${warning}`, toastOptions);
        });
      }

      toast.success(message || 'Template created successfully.');
      setTimeout(() => {
        navigate(`/templates/${templateId}`);
      }, 3000);

      return {
        isSuccess: true,
        templateId: templateId ?? null,
        message: message || 'Template created successfully.',
        warnings,
        errors: [],
        baseTemplateId: payload?.baseTemplateId,
        name: payload?.name,
      };
    } catch (rejected) {
      const rejectedMessage = getRejectedMessage(rejected);
      const audienceProblems = getCopyAudienceProblems(rejected);
      if (!audienceProblems) {
        toast.error(rejectedMessage);
      }
      return {
        isSuccess: false,
        templateId: null,
        message: rejectedMessage,
        warnings: [],
        errors: [rejectedMessage],
        baseTemplateId: payload?.baseTemplateId,
        name: payload?.name,
        retryPayload: payload,
        audienceProblems,
      };
    }
  };

  return { onCreateTemplate, isLoading };
};
